import { LucideCheck, LucideChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";
import { RequiredAsterisk } from "./required-asterisk";
import { Button } from "alurkerja-ui";
import { FieldValues, Path, useFormContext } from "react-hook-form";
import { useState } from "react";

interface FieldComboboxProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label: string;
  hideLabel?: boolean;
  options: {
    value: TFormValues[keyof TFormValues];
    label: string;
  }[];
  search: string;
  setSearch: (value: string) => void;
  onChange?: (option: {
    value: TFormValues[keyof TFormValues];
    label: string;
  }) => void;
  isDisabled?: boolean;
  hasRequiredAsterisk?: boolean;
  description?: React.ReactNode;
}
export function FieldCombobox<TFormValues extends FieldValues>({
  name,
  label,
  hideLabel,
  options,
  isDisabled = false,
  hasRequiredAsterisk = false,
  search,
  setSearch,
  onChange: handleChange,
  description,
}: FieldComboboxProps<TFormValues>) {
  const [isOpen, setIsOpen] = useState(false);
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="w-full break-inside-avoid-column">
          <FormLabel className={hideLabel ? "sr-only" : undefined}>
            {label}
          </FormLabel>
          {hasRequiredAsterisk && <RequiredAsterisk />}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  role="combobox"
                  disabled={isDisabled}
                  className="flex w-full items-center gap-x-2 rounded-md border bg-white text-sm text-black disabled:border-[#E1E1E1] disabled:bg-[#F3F4F6] disabled:text-black disabled:!opacity-100 dark:bg-neutral-50"
                  title="Search"
                  onClick={() => setSearch("")}
                >
                  <LucideChevronsUpDown className="-ml-6" />
                  {options?.find(
                    (option) =>
                      JSON.stringify(option.value) ===
                      JSON.stringify(field.value),
                  )?.label ?? ""}
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent align="start">
              <Command>
                <CommandInput
                  placeholder="Search..."
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearch(e.target.value);
                  }}
                />
                {search?.length ? (
                  <CommandEmpty>No data found.</CommandEmpty>
                ) : null}
                <CommandGroup className="max-h-48 overflow-auto">
                  {options?.map((option) => (
                    <CommandItem
                      key={option.label ?? option.value}
                      value={`${option.label}`}
                      onSelect={() => {
                        form.setValue(name, option.value);
                        handleChange?.(option);
                        setIsOpen(false);
                      }}
                    >
                      {`${option.label}`}
                      <LucideCheck
                        className={`ml-auto ${
                          JSON.stringify(option.value) ===
                          JSON.stringify(field.value)
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
